'use client';

import React, { useState } from 'react';
import AnimatedLink from '@/components/AnimatedLink/AnimatedLink';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import Carousel from '@/components/Carousel/Carousel';
import Matterport, {
  MatterportButton,
} from '@/components/Matterport/Matterport';
import { DescriptionType, ImageType } from '@/utils/common.types';

interface Props {
  description: DescriptionType;
  imagesCollection: ImageType[];
  isDigitalModel: boolean;
  showAnimatedLink?: boolean;
  matterportId?: string;
  modelTitle?: string;
  modelLength?: number;
  numberBaths?: number;
  numberBeds?: number;
  title?: string;
  modelWidth?: number;
}

const PhotoGallery = ({
  title,
  description,
  imagesCollection = [],
  isDigitalModel = false,
  showAnimatedLink = true,
  matterportId,
  modelTitle,
  numberBeds,
  numberBaths,
  modelLength,
  modelWidth,
}: Props) => {
  const [showMatterport, setShowMatterport] = useState(false);

  const handleMatterportClick = () => {
    setShowMatterport(!showMatterport);
  };

  return (
    <section className="mx-auto my-[30px] w-mobile max-w-standard text-hbg-blue">
      {/** Temporary Breadcrumb until a more permanent solution is implemented */}
      {showAnimatedLink && (
        <div className="my-5">
          <AnimatedLink
            buttonUrl="/crossmod#photo-grid-anchor"
            title="Back to Crossmod®"
            iconFlip={true}
            iconPosition="left"
            buttonOpenNewWindow={false}
          />
        </div>
      )}
      {title && (
        <div className="mb-[30px] font-acumin text-[22px] md:text-[26px] lg:text-[35px]">
          <h3 className="text-hbg-blue">{title}</h3>
        </div>
      )}
      {description && (
        <div className="mb-[30px] text-[1rem] lg:text-[1.15rem]">
          {ContentfulRichText(description)}
        </div>
      )}
      {isDigitalModel && (
        <div className="text-black mb-[15px] text-black-standard md:flex md:flex-row">
          {modelTitle && (
            <h4 className="text-[20px] font-bold md:mr-5 md:mt-[-5px] md:text-[24px]">
              {modelTitle}
            </h4>
          )}
          <div className="flex flex-row">
            {numberBeds && <p className="mr-7">{numberBeds} beds</p>}
            {numberBaths && (
              <p className="mr-7 list-item list-disc">{numberBaths} baths</p>
            )}
            {modelLength && modelWidth && (
              <p className="mr-7 list-item list-disc">
                {modelLength * modelWidth} sq ft
              </p>
            )}
          </div>
        </div>
      )}
      {imagesCollection?.length > 0 && (
        <div className="md:relative">
          <Carousel gallery={imagesCollection} />
          {matterportId && (
            <div className="md:absolute md:left-4 md:top-4">
              <MatterportButton onClick={() => handleMatterportClick()} />
            </div>
          )}
        </div>
      )}
      {showMatterport && (
        <Matterport
          matterportId={matterportId}
          onClick={() => handleMatterportClick()}
        />
      )}
    </section>
  );
};

export default PhotoGallery;
